import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Calendar, Trophy, Ticket, Play, Mail } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";

export default function RaffleGame() {
  const { toast } = useToast();
  const [isDrawing, setIsDrawing] = useState(false);
  const [winner, setWinner] = useState<string | null>(null);

  const handleDraw = () => {
    setIsDrawing(true);
    setWinner(null);
    
    // 30 second animation simulation
    setTimeout(() => {
      setWinner("Ticket #42 - Sarah Smith");
      setIsDrawing(false);
      toast({
        title: "Winner selected!",
        description: "Results have been emailed to all participants",
      });
    }, 3000); // Shortened for demo, would be 30s in production
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-3xl font-bold text-foreground">Digital Raffle</h1>
        <p className="text-muted-foreground mt-2">
          Create and manage digital raffles with automated draws
        </p>
        <Badge className="mt-2 bg-primary">Standard Plan Feature</Badge>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Create Raffle */}
        <Card className="shadow-card">
          <CardHeader>
            <CardTitle>Create New Raffle</CardTitle>
            <CardDescription>Set up your raffle with the wizard</CardDescription>
          </CardHeader>
          <CardContent>
            <Dialog>
              <DialogTrigger asChild>
                <Button className="w-full" size="lg">
                  <Ticket className="h-4 w-4 mr-2" />
                  Start Raffle Wizard
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>Create Raffle - Step 1 of 4</DialogTitle>
                  <DialogDescription>Set up your raffle details</DialogDescription>
                </DialogHeader>
                <form className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="raffle-name">Raffle Name</Label>
                    <Input id="raffle-name" placeholder="e.g., Summer Fair Prize Draw" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="raffle-start">Start Date</Label>
                      <Input id="raffle-start" type="date" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="raffle-end">Draw Date</Label>
                      <Input id="raffle-end" type="date" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="raffle-prizes">Prizes (one per line)</Label>
                    <Textarea 
                      id="raffle-prizes" 
                      placeholder="1st Prize: £100 Voucher&#10;2nd Prize: Hamper&#10;3rd Prize: Wine Bottle"
                      rows={4}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="raffle-price">Ticket Price</Label>
                      <Input id="raffle-price" placeholder="£1.00" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="raffle-max">Max Tickets</Label>
                      <Input id="raffle-max" type="number" placeholder="500" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>
                      <input type="checkbox" className="mr-2" required />
                      I confirm this raffle complies with gambling license requirements
                    </Label>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="raffle-event">Link to Event (Optional)</Label>
                    <Input id="raffle-event" placeholder="Select event..." />
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button type="button" variant="outline">Cancel</Button>
                    <Button type="submit">Next Step</Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>

            <div className="mt-4 p-4 bg-muted/30 rounded-lg">
              <h4 className="font-semibold text-sm mb-2">Raffle Features:</h4>
              <ul className="text-sm space-y-1 text-muted-foreground">
                <li>• Step-by-step wizard setup</li>
                <li>• Parents can buy tickets online</li>
                <li>• Automatic draw with 30s animation</li>
                <li>• Results emailed to all participants</li>
                <li>• Winners shown on fundraising page</li>
              </ul>
            </div>
          </CardContent>
        </Card>

        {/* Active Raffle */}
        <Card className="shadow-card">
          <CardHeader>
            <CardTitle>Active Raffle</CardTitle>
            <CardDescription>Summer Fair Prize Draw</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="p-3 bg-muted/30 rounded-lg">
                <p className="text-sm text-muted-foreground">Tickets Sold</p>
                <p className="text-2xl font-bold text-primary">247</p>
              </div>
              <div className="p-3 bg-muted/30 rounded-lg">
                <p className="text-sm text-muted-foreground">Tickets Remaining</p>
                <p className="text-2xl font-bold">253</p>
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Progress</span>
                <span className="font-medium">49.4%</span>
              </div>
              <div className="h-3 bg-secondary rounded-full overflow-hidden">
                <div className="h-full bg-primary transition-all" style={{ width: "49.4%" }} />
              </div>
            </div>

            <div className="pt-4 border-t border-border">
              <div className="flex items-center gap-2 mb-3">
                <Calendar className="h-4 w-4 text-primary" />
                <span className="text-sm font-medium">Draw Date: June 15, 2025</span>
              </div>
              <div className="flex items-center gap-2 mb-4">
                <Trophy className="h-4 w-4 text-primary" />
                <span className="text-sm font-medium">3 Prizes Available</span>
              </div>

              {!winner && !isDrawing && (
                <Button className="w-full" size="lg" onClick={handleDraw}>
                  <Play className="h-4 w-4 mr-2" />
                  Start Draw Animation
                </Button>
              )}

              {isDrawing && (
                <div className="text-center py-8">
                  <div className="animate-spin inline-block w-12 h-12 border-4 border-primary border-t-transparent rounded-full mb-4" />
                  <p className="font-semibold">Drawing winner...</p>
                  <p className="text-sm text-muted-foreground">Shuffling tickets</p>
                </div>
              )}

              {winner && !isDrawing && (
                <div className="text-center py-6 bg-gradient-primary rounded-lg text-primary-foreground">
                  <Trophy className="h-12 w-12 mx-auto mb-3" />
                  <p className="font-semibold text-lg mb-1">Winner!</p>
                  <p className="text-2xl font-bold mb-4">{winner}</p>
                  <Button variant="secondary">
                    <Mail className="h-4 w-4 mr-2" />
                    Email Results
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Previous Raffles */}
      <Card className="shadow-card">
        <CardHeader>
          <CardTitle>Previous Raffles</CardTitle>
          <CardDescription>View past raffle results</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="p-4 border border-border rounded-lg">
              <div className="flex items-start justify-between">
                <div>
                  <h4 className="font-semibold">Easter Egg Raffle</h4>
                  <p className="text-sm text-muted-foreground">Completed: April 10, 2025</p>
                  <p className="text-sm mt-2">Winner: Ticket #123 - Emma Wilson</p>
                </div>
                <Badge variant="secondary">Completed</Badge>
              </div>
            </div>
            <div className="p-4 border border-border rounded-lg">
              <div className="flex items-start justify-between">
                <div>
                  <h4 className="font-semibold">Christmas Hamper Draw</h4>
                  <p className="text-sm text-muted-foreground">Completed: December 20, 2024</p>
                  <p className="text-sm mt-2">Winner: Ticket #89 - Michael Brown</p>
                </div>
                <Badge variant="secondary">Completed</Badge>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
